// Where things sit on the ground: the painter's-algorithm depth key, and the
// contact shadow that stops a lit clay sprite reading as a sticker pasted onto
// the tiles.
//
// Both answer the same question — "which point of the 2:1 ground plane is this
// object standing on?" — so they live together and share one convention: an
// actor's depth is keyed by the fractional tile coordinate it is DRAWN at.
// Keying a bird by its integer tile while drawing it at the tile centre put
// every bird half a tile too far back, which is the width of a coop's edge and
// enough to swap a feeder and a hen.

import { depthSort, TILE_HEIGHT, TILE_WIDTH } from './iso';
import type { SpriteMeta } from './spriteMeta';

/**
 * A circle lying flat on a 2:1 ground plane projects to an ellipse this flat.
 * Every shadow here is an ellipse because that is what a round contact patch
 * looks like once the plane it lies on is squashed.
 */
export const GROUND_SQUASH = TILE_HEIGHT / TILE_WIDTH;

/**
 * Half-extents of the contact shadow for a tile footprint, in world pixels.
 *
 * A w×h run of tiles projects to a diamond spanning `(w+h) * TILE_WIDTH / 2`
 * across, so the tightest ellipse that stays on the structure's own ground is
 * half of that, shrunk a little further so it reads as a soft contact patch
 * rather than a painted-on outline of the footprint.
 */
export function footprintShadow(
  footW: number,
  footH: number,
): { rx: number; ry: number } {
  const rx = ((footW + footH) * TILE_WIDTH) / 4 * SHADOW_COVERAGE;
  return { rx, ry: rx * GROUND_SQUASH };
}

/**
 * Fraction of the footprint the shadow actually covers. Below 1 so the patch
 * has a soft edge inside its own ground instead of a hard rim.
 */
const SHADOW_COVERAGE = 0.8;

/**
 * Half-extents of the contact shadow for a bird, in world pixels.
 *
 * Derived from the pipeline's measurement rather than from the image box: the
 * art arrives with whatever headroom the render happened to include, so a
 * bounding box moves the shadow every time a sprite is re-exported. `standHeight`
 * is pivot-to-crown, which is scale-invariant, so a 2x re-export lands the exact
 * same shadow — and the ellipse is centred on the pivot, which is the feet.
 *
 * The ratio is under half the drawn width because a bird's body overhangs the
 * feet that carry it; a shadow as wide as the widest breed would read as a
 * puddle.
 */
export function birdShadow(meta: SpriteMeta, drawnHeight: number): { rx: number; ry: number } {
  const scale = drawnHeight / (meta.standHeight ?? meta.h);
  return shadowForDrawnWidth(meta.w * scale);
}

/**
 * The same patch for a bird whose drawn width is already known — the fallback
 * for a sprite with no measurement, where the draw call has scaled the image
 * itself and that scale is the only description of the bird that exists.
 * Tracking the drawn width keeps the shadow honest about the sprite that is
 * actually on screen instead of guessing from a different formula.
 */
export function shadowForDrawnWidth(drawnWidth: number): { rx: number; ry: number } {
  const rx = drawnWidth * BIRD_SHADOW_RATIO;
  return { rx, ry: rx * GROUND_SQUASH };
}

const BIRD_SHADOW_RATIO = 0.42;

/**
 * Depth of an actor from the tile coordinate it is drawn at.
 *
 * Every ground actor — bird, structure, shadow — goes through here, so the
 * sort key and the draw position can never drift apart again.
 */
export function actorDepth(tx: number, ty: number, tz = 0): number {
  return depthSort(tx, ty, tz);
}

/**
 * Depth of a shadow: its owner's depth, nudged just behind it.
 *
 * The nudge is far smaller than the gap between any two distinct ground depths
 * (half a tile is 8, so nothing real lives between 0 and 0.5), which means a
 * shadow can never sort in front of an actor it is genuinely behind, and can
 * never sort ahead of its own sprite. It does not rely on the two being
 * siblings in the same container, so the relationship survives either way.
 */
export const SHADOW_BEHIND = 0.5;

export function shadowDepth(ownerDepth: number): number {
  return ownerDepth - SHADOW_BEHIND;
}
